<template>
  <div class="filter-tag" :class="rootClasses">
    <cv-tag
      class="bx--text-truncate--end"
      :filter="filter"
      :label="label"
      kind="blue"
      :disabled="disabled"
      :title="label"
      @remove="handleRemove"
    />
    <span v-if="hasMultiple" class="count" :data-count="count" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    label?: string;
    count?: number;
    disabled?: boolean;
    filter?: boolean;
  }>(),
  {
    label: "",
    count: 0,
    disabled: false,
    filter: false,
  },
);

const emit = defineEmits<(e: "remove") => void>();

const hasMultiple = computed(() => props.count > 1);
const hasNonSingleDigitCount = computed(() => props.count && Math.floor(props.count / 10) > 0);
const rootClasses = computed(() => ({
  _multiple: hasMultiple.value,
  _filter: props.filter,
  "_non-single": hasNonSingleDigitCount.value,
}));

function handleRemove() {
  emit("remove");
}
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.filter-tag {
  position: relative;
  margin: $spacing-02 $spacing-03 $spacing-02 0;

  .bx--tag {
    @include carbon--type-style("label-01", true);
    margin: 0;
    background-color: tint($tag-01, 70%);
    color: $state-neutral-text;
    border: 2px solid transparent;

    .bx--tag__label {
      // To fit 20 characters of the letter "E" according to Figma
      max-width: 165px;
    }

    .bx--tag__close-icon {
      &:hover {
        background-color: tint($tag-01, 50%);
      }
      &:focus {
        box-shadow: none;
      }
    }
  }

  &._multiple {
    // Right padding to give space allowance for the count
    &._non-single {
      .bx--tag__label {
        padding-right: $spacing-09;
      }
    }

    .bx--tag__label {
      padding-right: $spacing-08;
    }

    .bx--tag {
      background-color: tint($tag-01, 90%);
    }

    &._filter {
      &:active {
        border-color: $interactive-01;
      }

      > .count {
        right: 1.5rem;
      }
    }

    > .count {
      @include carbon--type-style("label-02", true);
      position: absolute;
      right: 0.5rem;
      top: 50%;
      transform: translateY(-50%);

      &:after {
        content: attr(data-count);
        display: inline-block;
        border-radius: 20px;
        padding: $spacing-02 $spacing-04;
        margin: 0 $spacing-02;
        background-color: tint($tag-01, 50%);
        color: $state-neutral-text;
        text-align: center;
      }
    }
  }

  &.data-assistant {
    .bx--tag {
      background-color: tint($tag-04, 75%);
      color: $state-partial-text;

      .bx--tag__close-icon {
        &:hover {
          background-color: tint($tag-04, 50%);
        }
      }
    }
  }
}
</style>
